// Fetch ETF/fund top holdings and sector weights via server-side Yahoo Finance proxy
// Auth (crumb + cookies) is handled server-side in the Vite plugin
const SKIP_TICKERS = new Set(['CASH', 'MARGIN']);

// In-memory cache to avoid re-fetching during a session
const holdingsCache = {};

/**
 * Fetch topHoldings for a single ticker via the authenticated server-side proxy.
 * Returns { holdings: [...], sectorWeights: [...], equity/bond/cash positions } or null.
 */
async function fetchSingleHoldings(ticker) {
  if (holdingsCache[ticker]) return holdingsCache[ticker];
  try {
    const res = await fetch(`/api/yf-quote-summary/${encodeURIComponent(ticker)}`);
    if (!res.ok) return null;
    const json = await res.json();
    const topHoldings = json.quoteSummary?.result?.[0]?.topHoldings;
    if (!topHoldings) return null;

    const result = {
      holdings: (topHoldings.holdings || []).map((h) => ({
        symbol: h.symbol,
        name: h.holdingName,
        percent: h.holdingPercent?.raw ?? h.holdingPercent ?? 0,
      })),
      sectorWeights: (topHoldings.sectorWeightings || []).flatMap((sw) =>
        Object.entries(sw).map(([sector, weight]) => ({
          sector: sector.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          weight: weight?.raw ?? weight ?? 0,
        }))
      ),
      stockPosition: topHoldings.stockPosition?.raw ?? topHoldings.stockPosition ?? 0,
      bondPosition: topHoldings.bondPosition?.raw ?? topHoldings.bondPosition ?? 0,
      cashPosition: topHoldings.cashPosition?.raw ?? topHoldings.cashPosition ?? 0,
      otherPosition: topHoldings.otherPosition?.raw ?? topHoldings.otherPosition ?? 0,
    };

    holdingsCache[ticker] = result;
    return result;
  } catch {
    return null;
  }
}

/**
 * Fetch holdings data for multiple tickers.
 * Returns a map: { 'VTI': { holdings, sectorWeights, ... }, ... }
 */
export async function fetchAllHoldings(tickers) {
  const unique = [...new Set(tickers.map((t) => t.toUpperCase()))].filter(
    (t) => !SKIP_TICKERS.has(t)
  );
  if (unique.length === 0) return {};

  const results = await Promise.allSettled(
    unique.map(async (ticker) => {
      const data = await fetchSingleHoldings(ticker);
      return { ticker, data };
    })
  );

  const map = {};
  for (const result of results) {
    if (result.status === 'fulfilled' && result.value.data) {
      map[result.value.ticker] = result.value.data;
    }
  }
  return map;
}
